import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import downloadReport from '../utils/downloadReport';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import {
  CARD,
  FormStatusBadge,
  INPUT,
  PRIMARY,
  SECONDARY,
  STUDENT_STATUSES,
  ProgressBar,
  dayFromDueAt,
  dueAtFromDay,
  formatDateTime,
  formatDue,
} from '../components/forms/formsText';
import {
  PAGE_SIZE,
  exportPath,
  formsError,
  useArchiveAssignment,
  useAssignment,
  useAssignmentStudents,
  useRemind,
  useUpdateAssignment,
} from '../hooks/useFormAssignments';

const STATUS_STYLES = {
  submitted: 'bg-green-50 text-green-700',
  in_progress: 'bg-amber-50 text-amber-700',
  not_started: 'bg-gray-100 text-gray-600',
};

// The backend refuses reminders for archived assignments and closed forms.
const remindBlockedReason = (a) => {
  if (a.status !== 'active') return 'Restore the form to send reminders.';
  if (a.form_status === 'closed')
    return 'The form is closed, so reminders can’t be sent.';
  return null;
};

const remindedText = (count) =>
  count
    ? `Reminder sent to ${count} ${count === 1 ? 'student' : 'students'}.`
    : 'No reminder sent: already reminded today.';

function EditDialog({ assignment, open, onClose }) {
  const update = useUpdateAssignment(assignment.id);
  const [title, setTitle] = useState(assignment.title || '');
  const [message, setMessage] = useState(assignment.message || '');
  const [dueDay, setDueDay] = useState(dayFromDueAt(assignment.due_at));
  useEffect(() => {
    if (!open) return;
    setTitle(assignment.title || '');
    setMessage(assignment.message || '');
    setDueDay(dayFromDueAt(assignment.due_at));
  }, [open, assignment]);
  const save = () =>
    update.mutate(
      {
        title: title.trim(),
        message: message.trim(),
        due_at: dueAtFromDay(dueDay),
      },
      {
        onSuccess: () => {
          toast.success('Saved.');
          onClose();
        },
        onError: (err) =>
          toast.error(formsError(err, "We couldn't save the changes.")),
      }
    );
  return (
    <Modal modalOpen={open} onClose={onClose} title="Edit assignment" small>
      <div className="space-y-3 text-left">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={INPUT}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Message to students
          <textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={INPUT}
          />
        </label>
        <div className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          <label htmlFor="due-day">Due date (end of day, Kenya time)</label>
          <div className="flex items-center gap-2">
            <input
              id="due-day"
              type="date"
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value)}
              className={`${INPUT} w-48`}
            />
            {dueDay && (
              <button
                type="button"
                onClick={() => setDueDay('')}
                className="text-xs text-claret-500 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={SECONDARY}>
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={update.isLoading || !title.trim()}
            className={PRIMARY}
          >
            {update.isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Students({ assignment }) {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const remind = useRemind(assignment.id);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, isLoading, error } = useAssignmentStudents(
    assignment.id,
    { status, q },
    offset
  );
  const students = data?.students ?? [];
  const total = data?.total ?? 0;
  const counts = assignment.counts ?? {};
  const blocked = remindBlockedReason(assignment);

  const remindOne = (student) => {
    setBusyId(student.student_id);
    remind.mutate([student.student_id], {
      onSuccess: (r) => toast.success(remindedText(r?.count ?? 0)),
      onError: (err) =>
        toast.error(formsError(err, "We couldn't send the reminder.")),
      onSettled: () => setBusyId(null),
    });
  };

  return (
    <section className={`${CARD} space-y-3 p-5`}>
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: '', label: 'All', count: counts.members },
          ...STUDENT_STATUSES.map((s) => ({ ...s, count: counts[s.key] })),
        ].map((s) => (
          <button
            key={s.key || 'all'}
            type="button"
            aria-pressed={status === s.key}
            onClick={() => {
              setStatus(s.key);
              setOffset(0);
            }}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
              status === s.key
                ? 'border-claret-500 bg-claret-500 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {s.label}
            {s.count !== undefined && (
              <span
                className={`rounded-full px-1.5 text-[10px] font-semibold ${
                  status === s.key ? 'bg-white/20' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {s.count.toLocaleString()}
              </span>
            )}
          </button>
        ))}
        <input
          type="search"
          aria-label="Search students"
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${INPUT} ml-auto w-56`}
        />
      </div>
      {isLoading && (
        <div
          className="h-32 rounded bg-gray-100 animate-pulse"
          aria-label="Loading"
        />
      )}
      {error && (
        <p className="text-sm text-gray-600">
          {formsError(error, "We couldn't load the students.")}
        </p>
      )}
      {data && !error && total === 0 && (
        <p className="text-sm text-gray-600">No students match.</p>
      )}
      {data && !error && total > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-2 pl-4 pr-2 font-medium">Student</th>
                <th className="px-2 py-2 font-medium">Status</th>
                <th className="px-2 py-2 font-medium">Submitted</th>
                <th className="px-2 py-2 font-medium">Last reminded</th>
                <th className="py-2 pl-2 pr-4" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr
                  key={s.student_id}
                  className="border-b border-gray-100 last:border-0"
                >
                  <td className="py-2 pl-4 pr-2">
                    <span className="text-gray-900">{s.name || '–'}</span>
                    <span className="block text-xs text-gray-500">
                      {s.email}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        STATUS_STYLES[s.status] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {STUDENT_STATUSES.find((x) => x.key === s.status)
                        ?.label || s.status}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-600">
                    {s.submitted_at ? formatDateTime(s.submitted_at) : '–'}
                    {s.receipt_number && (
                      <span className="block text-gray-400">
                        Receipt {s.receipt_number}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-600">
                    {s.last_reminded_at
                      ? formatDateTime(s.last_reminded_at)
                      : '–'}
                  </td>
                  <td className="py-2 pl-2 pr-4 text-right">
                    {s.status !== 'submitted' && (
                      <button
                        type="button"
                        onClick={() => remindOne(s)}
                        disabled={remind.isLoading || Boolean(blocked)}
                        title={blocked || undefined}
                        className="text-xs text-claret-500 hover:underline disabled:opacity-50"
                      >
                        {busyId === s.student_id ? 'Sending…' : 'Remind'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {offset + 1}–{Math.min(offset + students.length, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(offset - PAGE_SIZE)}
              className={SECONDARY}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className={SECONDARY}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default function FormAssignmentDetail() {
  const { assignmentId } = useParams();
  const { data: a, isLoading, error } = useAssignment(assignmentId);
  const archive = useArchiveAssignment(assignmentId);
  const remind = useRemind(assignmentId);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'remind' | 'archive' | 'restore'
  const [exporting, setExporting] = useState(false);

  if (isLoading) {
    return (
      <MainContent full>
        <div className={`${CARD} h-64 animate-pulse`} aria-label="Loading" />
      </MainContent>
    );
  }
  if (error || !a) {
    return (
      <MainContent full>
        <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
          {formsError(error, "We couldn't load this assignment.")}
        </div>
      </MainContent>
    );
  }

  const notSubmitted =
    (a.counts?.not_started ?? 0) + (a.counts?.in_progress ?? 0);
  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadReport(exportPath(a.id), 'form-responses.csv', {
        client: axiosInstancev2,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };
  const run = () => {
    if (confirm === 'remind') {
      remind.mutate(undefined, {
        onSuccess: (r) => toast.success(remindedText(r?.count ?? 0)),
        onError: (err) =>
          toast.error(formsError(err, "We couldn't send the reminders.")),
        onSettled: () => setConfirm(null),
      });
    } else {
      archive.mutate(confirm === 'archive', {
        onSuccess: () =>
          toast.success(
            confirm === 'archive'
              ? 'Archived. Students no longer see it.'
              : 'Restored. Students can see it again.'
          ),
        onError: (err) =>
          toast.error(formsError(err, "We couldn't change it.")),
        onSettled: () => setConfirm(null),
      });
    }
  };
  const busy = remind.isLoading || archive.isLoading;

  return (
    <MainContent full>
      <div className="space-y-5 pb-10">
        <header className="space-y-2">
          <Link to="/forms" className="text-xs text-claret-500 hover:underline">
            ← Forms
          </Link>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-gray-900">
                {a.title}
              </h1>
              <p className="text-sm text-gray-600">
                {a.cohort?.name} · {formatDue(a.due_at)} ·{' '}
                <FormStatusBadge status={a.form_status} />
                {a.status === 'archived' && (
                  <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
                    Archived
                  </span>
                )}
              </p>
              {a.title !== a.form_title && (
                <p className="text-xs text-gray-500">Form: {a.form_title}</p>
              )}
              {a.message && (
                <p className="max-w-2xl text-sm text-gray-700">{a.message}</p>
              )}
              <p className="text-[11px] text-gray-400">
                Assigned {formatDateTime(a.created_at)}
                {a.created_by?.name && ` by ${a.created_by.name}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {a.form_url && (
                <a
                  href={a.form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${SECONDARY} inline-flex items-center gap-1`}
                >
                  View responses on forms
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </a>
              )}
              <button
                type="button"
                onClick={exportCsv}
                disabled={exporting}
                className={`${SECONDARY} inline-flex items-center gap-1`}
              >
                <DownloadIcon fontSize="small" />
                {exporting ? 'Exporting…' : 'Export'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className={SECONDARY}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() =>
                  setConfirm(a.status === 'archived' ? 'restore' : 'archive')
                }
                className={SECONDARY}
              >
                {a.status === 'archived' ? 'Restore' : 'Archive'}
              </button>
              {notSubmitted > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirm('remind')}
                  disabled={Boolean(remindBlockedReason(a))}
                  title={remindBlockedReason(a) || undefined}
                  className={PRIMARY}
                >
                  Remind everyone not submitted
                </button>
              )}
            </div>
          </div>
        </header>

        <section className={`${CARD} p-5`}>
          <ProgressBar counts={a.counts} />
        </section>

        <Students assignment={a} />
      </div>

      <EditDialog
        assignment={a}
        open={editing}
        onClose={() => setEditing(false)}
      />
      <Modal
        modalOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={
          {
            remind: 'Send reminders?',
            archive: 'Archive this form?',
            restore: 'Restore this form?',
          }[confirm] || ''
        }
        small
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          {confirm === 'remind' && (
            <p>
              Remind {notSubmitted.toLocaleString()}{' '}
              {notSubmitted === 1 ? 'student' : 'students'} who haven&apos;t
              submitted, in-app and by email. Anyone already reminded today is
              skipped.
            </p>
          )}
          {confirm === 'archive' && (
            <p>
              Students won&apos;t see it any more. Their responses are kept, and
              you can restore it.
            </p>
          )}
          {confirm === 'restore' && (
            <p>Students in {a.cohort?.name} will see it again.</p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className={SECONDARY}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={run}
              disabled={busy}
              className={PRIMARY}
            >
              {busy
                ? 'Working…'
                : {
                    remind: 'Send reminders',
                    archive: 'Archive',
                    restore: 'Restore',
                  }[confirm]}
            </button>
          </div>
        </div>
      </Modal>
    </MainContent>
  );
}
